import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { allCountries } from 'country-telephone-data';
import { AsYouType, CountryCode } from 'libphonenumber-js';
import { combineLatest, delayWhen, filter, map, Observable, Subject } from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { PropsOf } from '@blaze-runtime/engine-domain';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import {
  ColumnDataType,
  DurationColumnFormat,
  NumberPrecision,
} from '@blaze-shared/api-contracts-domain';
import { durationMasks } from '@blaze-shared/datasets-domain';
import {
  type ElementFormState,
  ElementVerticalAlignment,
  InputElementMetaUiFeatures,
  makeFontStyles,
  makeFontStyleStyles,
  TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { getBackground } from '@blaze-shared/elements-domain';
import { getDisplayStringFromLinkValue } from '@blaze-shared/schema-domain';
import {
  type ElementSchema,
  hasNoneDefaultValue,
  isDatasetLinkValue,
  type RuntimeEventType,
} from '@blaze-shared/schema-domain';
import {
  createGeoPointFromString,
  durationInputTransform,
  durationPaste,
  formatDuration,
  formatDurationForSaving,
  formatGeoPoint,
  geoPointPasteEventHandler,
  geoPointValueBlur,
  geoPointValueChange,
  getDurationFormatFromDuration,
  isGeoPoint,
  resetSecureFieldFormControl,
  secureFieldInputBeforeInput,
  secureFieldTypeFocus,
} from '@blaze-shared/shared-feature';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import {
  digitRegex,
  GeoPoint,
  getCountryFromLocale,
  getCurrencyFormat,
  regexpMatchLength,
  useEmitTracker,
  userBrowserCountry,
} from '@blaze-shared/shared-util';
import { isNullish, type Nullish, roundToPrecision } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { initializeTinyMceConfig } from '../../utils/elements/tiny-mce.utils';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { getPlaceholderText, getTinyMceToolbarElement, handleFormat } from './helpers.utils';
import { getInputValidationConfig, InputConfig } from './text-input-validation';

const EDITOR_TOOLBAR_CHECK_TIMEOUT = 30;
const EDITOR_TOOLBAR_CHECK_TRIES = 90;

type TextInputUpdateSource = 'input' | 'blur' | 'external';
type TextInputValue = Nullish<string | number | GeoPoint>;

interface TextInputUpdatePayload {
  value: TextInputValue;
  source: TextInputUpdateSource;
}

@UntilDestroy()
@Component({
  selector: 'be-text-input',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextInputComponent
  extends CanvasElementBaseComponent<'textInput'>
  implements CanvasElementComponent<'textInput'>, OnChanges, OnInit, OnDestroy
{
  private elRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() value?: TextInputValue;
  @Input() formState?: ElementFormState;
  @ViewChild('inputText') inputText?: ElementRef<HTMLInputElement>;
  @ViewChild('textArea') textArea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild(EditorComponent) editor?: EditorComponent;
  @HostBinding('attr.data-prevented') submitPrevented: boolean | undefined = undefined;

  private dataValueFeaturesFetched = false;
  private isFocusedValue = false;
  private readonly outputEmitTracker = useEmitTracker();
  private wasEmitted = false;
  private defaultValue?: string | number | GeoPoint;
  private areaClickEvent?: MouseEvent;
  private readonly triggerUpdate$ = new Subject<TextInputUpdatePayload>();
  private readonly dataValue = this.getDataInput('value');

  readonly elementKeyRoot = 'b-ta';
  wrapperSectionKey!: string;
  editorKey!: string;
  editorToolbarKey!: string;

  labelEnabled!: boolean;
  labelText!: string;
  precision?: NumberPrecision;

  placeholder!: string;
  format?: TableColumnFormat;
  currency!: string;
  prefix!: string;
  suffix!: string;
  maximum?: number;
  minimum?: number;
  isReadonly = false;
  culture?: string;

  secureFieldType: 'text' | 'password' = 'password';
  passwordIconStyles: Record<string, string> = {};
  editorConfig: EditorComponent['init'];
  isLiveEdit = false;

  inputConfig: InputConfig = getInputValidationConfig('text');
  formControl = new UntypedFormControl('');
  elementFormat!: TableColumnFormat;
  durationFormat!: DurationColumnFormat;
  inputStep?: number | 'any';
  controlStyles!: Record<string, string>;
  validationStyles!: Record<string, string>;
  inputWrapperStyles!: Record<string, string>;
  inputStyles!: Record<string, string>;
  formId?: string;

  submitOnEnter = false;
  changeOnBlur = false;
  labelVerticalAlign: ElementVerticalAlignment | undefined;
  textareaContainerClasses$?: Observable<string[]>;

  set isFocused(value: boolean) {
    this.isFocusedValue = value;
    if (this.props?.style.input?.border.selectorType !== 'none') {
      const color = value
        ? this.props?.style.general?.accent.color1
        : this.props?.style.input?.border.color1;
      this.inputWrapperStyles['border'] = `1px solid ${color}`;
      this.cdr.detectChanges();
    }
    if (value === false) {
      const data = handleFormat(this.formControl.value, this.props?.content?.input);
      this.updateInputControlAndPreformat(data);
      this.commitChange();
      const toolbar = getTinyMceToolbarElement();
      // ensure toolbar is hidden (sometimes it's left behind)
      if (toolbar) {
        toolbar.style.display = 'none';
      }
    }
  }
  get isFocused(): boolean {
    return this.isFocusedValue;
  }

  get isParagraph(): boolean {
    return this.elementFormat === 'paragraph';
  }
  get isRichText(): boolean {
    return this.elementFormat === 'richText';
  }
  get isMarkdown(): boolean {
    return this.elementFormat === 'markdown';
  }

  get durationMask(): string {
    return durationMasks[this.durationFormat];
  }

  get showErrorLabel(): string | boolean | undefined {
    return (
      this.hasValidationError ||
      (this.formControl.invalid && this.formControl.dirty && this.inputConfig.invalidMsg)
    );
  }

  get hasValidationError(): boolean | undefined {
    return (
      this.formState?.errors?.required ||
      this.formState?.errors?.valueFormat ||
      this.formState?.errors?.min ||
      this.formState?.errors?.max ||
      !!this.formState?.errors?.invalid
    );
  }

  get errorLabel(): string | undefined {
    return (
      this.inputConfig.invalidMsg ||
      this.formState?.messages?.required ||
      this.formState?.messages?.valueFormat ||
      this.formState?.messages?.min ||
      this.formState?.messages?.max ||
      this.formState?.messages?.invalid
    );
  }

  get editorBody(): HTMLElement | undefined {
    if (this.isRichText) {
      return this.editor?.editor?.bodyElement;
    }
    if (this.isMarkdown) {
      return this.textArea?.nativeElement;
    }
    return this.inputText?.nativeElement;
  }

  constructor() {
    super();
    this.editorConfig = initializeTinyMceConfig({
      initHandler: () => {
        if (this.areaClickEvent) {
          this.onAreaClicked(this.areaClickEvent);
        }
      },
      blurHandler: () => {
        this.isLiveEdit = false;
        this.controlStyles['min-height'] = '0';
        this.commitChange();
        this.cdr.detectChanges();
      },
      inputHandler: (content: string) => {
        this.triggerUpdate$.next({ value: content, source: 'input' });
      },
    });

    this.triggerUpdate$
      .pipe(
        delayWhen(() => this.getElementEventSchemaSet('change')),
        untilDestroyed(this),
      )
      .subscribe(({ value, source }) => {
        this.updateOutput(value, source);
      });

    this.dataValue.column$.pipe(filter(Boolean), untilDestroyed(this)).subscribe(() => {
      this.setDefaultEventSchema('change');
    });

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.bindingResults?.['value']) {
      this.setDefaultEventSchema('submit');
      this.setDefaultEventSchema('change');
    }

    if (changes?.['props']) {
      if (!this.wrapperSectionKey) {
        this.wrapperSectionKey = `section-${this.elementId}`;
        this.editorKey = `editor-${this.elementId}`;
        this.editorToolbarKey = `editor-${this.elementId}__toolbar`;
      }
      const { title, input } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;
      this.placeholder = getPlaceholderText(input, this.format);
      if (input?.format && ['currency', 'percentage', 'number'].includes(input.format)) {
        this.minimum = input?.minimum !== '' ? Number(input.minimum) : undefined;
        this.maximum = input?.maximum !== '' ? Number(input.maximum) : undefined;
      } else {
        this.minimum = undefined;
        this.maximum = undefined;
      }

      this.updateFormControl();
      this.updateStyles();
    }

    if (changes?.['props'] || changes?.['value']) {
      const previousProps = changes?.['props']?.previousValue || this.props;
      if (isDatasetLinkValue(this.value)) {
        this.value = getDisplayStringFromLinkValue(this.value.display);
      }

      // Precision change might affect the value
      if (this.shouldUpdateInputControl(this.value, previousProps, this.props)) {
        this.updateInputControlAndPreformat(this.value);
      } else {
        // Just reemit the value further
        this.triggerUpdate$.next({ value: this.value, source: 'external' });
      }
    }
  }

  ngOnInit(): void {
    this.setDefaultEventSchema('submit');

    this.textareaContainerClasses$ = combineLatest({
      props: this.props$,
      element: this.element$,
    }).pipe(
      map(({ props, element }) => {
        const isFixed = !!element.height?.fixed;
        const overflow = props?.content?.container?.overflow;
        const format = this.format ?? props?.content?.input?.format;
        if (format !== 'paragraph') {
          return [];
        }
        if (!isFixed) {
          return ['mode-full'];
        }
        return overflow === 'hidden' ? ['mode-fixed-clip'] : ['mode-fixed-scroll'];
      }),
    );

    /**
     * The updates to the form control invoke the output update logic
     */
    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const input = this.props?.content?.input;
      const options = {
        ...(!!input && this.props?.content?.input),
        phoneCountryCode: this.getCountryCode(),
      } as {
        format: TableColumnFormat | undefined;
        precision: NumberPrecision | undefined;
        phoneCountryCode?: CountryCode;
      };
      const data = handleFormat(value, options);
      this.formatInputPhoneNumberInput(data);
      this.triggerUpdate$.next({ value: data, source: 'input' });
    });

    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        this.formId = features.formElementId;
        this.submitOnEnter = features.submitOnEnter;
        this.changeOnBlur = features.changeOnBlur;

        this.precision = features.precision;
        this.format = features.format;
        this.currency = features.currency;
        this.durationFormat = features.duration ?? 'h:mm';
        this.placeholder = getPlaceholderText(
          this.props?.content?.input,
          this.format,
          features.duration,
        );
        this.defaultValue =
          features.column && hasNoneDefaultValue(features.column)
            ? undefined
            : features.column?.defaultValue;
        this.culture = features.column?.format?.culture;

        this.isReadonly = features.isReadOnly ?? false;

        this.dataValueFeaturesFetched = true;
        this.updateFormControl();
        this.updateInputControlAndPreformat(this.value);

        safeDetectChanges(this.cdr);
      });

    this.stylesCleanup.register(this.editorToolbarKey);
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose(this.editorToolbarKey);
  }

  override getDefaultEventSchema(eventType: RuntimeEventType): ElementSchema {
    const output = this.ioConfig?.outputs?.find((output) => output.eventType === eventType);
    const defaultSchema = output?.schema;
    const bindedSchemaColumn = this.dataValue.column;
    const dataType: ColumnDataType = ['currency', 'number', 'percentage'].includes(
      (bindedSchemaColumn?.dataType?.toLowerCase() ?? this.format) || 'String',
    )
      ? 'Number'
      : 'String';

    const updatedColumns = defaultSchema?.columns ? [...defaultSchema.columns] : [];
    const column = updatedColumns[0];
    if (column) {
      updatedColumns[0] = {
        ...column,
        columnType: dataType,
        dataType: dataType,
      };
    }
    return this.createElementSchema(eventType, updatedColumns, undefined, defaultSchema);
  }

  @HostListener('click')
  startLiveEdit(): void {
    if (!(this.isRichText || this.isMarkdown) || this.isReadonly) {
      return;
    }
    this.isLiveEdit = true;
    if (this.isMarkdown) {
      this.tryFocusEditor();
    }
  }

  onInputTransform(event: InputEvent, formControl: UntypedFormControl): void {
    if (this.elementFormat === 'duration') {
      durationInputTransform(event, formControl, this.durationFormat);
    } else if (this.elementFormat === 'geopoint') {
      geoPointValueChange(event, formControl);
    }
  }

  onInputPaste(event: ClipboardEvent, formControl: UntypedFormControl): void {
    if (this.elementFormat === 'duration') {
      durationPaste(event, formControl, this.durationFormat);
    } else if (this.elementFormat === 'geopoint') {
      geoPointPasteEventHandler(event, formControl);
    }
  }

  onInputBlur(formControl: UntypedFormControl): void {
    if (this.elementFormat === 'geopoint') {
      geoPointValueBlur(formControl);
    }
    this.handleMinMax(formControl.value);
  }

  secureFieldTypeFocusFn(stringInput: HTMLInputElement, formControl: UntypedFormControl): void {
    secureFieldTypeFocus(stringInput, formControl);
  }

  secureFieldInputBeforeInputFn(event: InputEvent, formControl: UntypedFormControl): void {
    secureFieldInputBeforeInput(event, formControl);
  }

  resetSecureFieldFormControlFn(formControl: UntypedFormControl): void {
    resetSecureFieldFormControl(formControl);
    this.changeSecureTextType('text');
    safeDetectChanges(this.cdr);
  }

  onAreaClicked(event: MouseEvent): void {
    this.tryToolbarStyling();
    const targetId = (event.target as HTMLElement).id;
    if (
      (!this.isRichText &&
        (targetId !== this.wrapperSectionKey || !this.inputText?.nativeElement)) ||
      this.isReadonly
    ) {
      return;
    }
    if (this.isRichText && (!this.isLiveEdit || !this.editor?.editor)) {
      this.areaClickEvent = event;
      this.onAreaFocused();
      return;
    }
    this.areaClickEvent = undefined;

    const editorBody = this.editorBody;
    if (!editorBody) {
      return;
    }

    const yDiff = editorBody.offsetTop - event.offsetY;
    if (yDiff > 0) {
      // clicked above
      if (yDiff < 32 && event.offsetX > editorBody.offsetWidth / 2) {
        // but in the right half and close enough to the editor
        this.makeEditorSelection(true);
      } else {
        this.makeEditorSelection();
      }
    } else if (event.offsetY > editorBody.offsetTop + editorBody.offsetHeight) {
      // clicked below
      this.makeEditorSelection(true);
    }
  }
  onAreaFocused(): void {
    if (this.isReadonly) {
      return;
    }

    // set min height to prevent widget height flicker
    this.controlStyles['min-height'] =
      `${this.elRef.nativeElement.querySelector('.control')?.clientHeight ?? 0}px`;

    this.startLiveEdit();
    this.tryFocusEditor();
  }

  onAreaBlur(): void {
    this.isLiveEdit = false;
    this.isFocused = false;
  }

  onEnterPressed(): void {
    if (this.isReadonly || !this.submitOnEnter || !this.formId) {
      this.submitPrevented = true;
      return;
    }
    this.output.emit({
      eventType: 'submit',
      data: this.formId,
      isInitialData: !this.wasEmitted,
      schema: this.getEventSchema('submit'),
    });
    this.wasEmitted = true;
  }

  reset(): void {
    this.outputEmitTracker.reset();
    this.wasEmitted = false;
    this.formControl.setValue('');
    this.secureFieldType = 'password';
    if (this.format === 'secureText') {
      this.resetSecureFieldFormControlFn(this.formControl);
    }
  }

  changeSecureTextType(type = this.secureFieldType): void {
    this.secureFieldType = type === 'text' ? 'password' : 'text';
  }

  onEditorMousedown(event: Event): void {
    this.tryToolbarStyling();
    markEventAsHandled(event);
  }

  onValueChanged(e: any): void {
    this.triggerUpdate$.next({ value: e.event.level.content, source: 'input' });
  }

  /**
   * This method recognizes whether a completely new value is set
   * or if it's being inputted by the user and goes through transient/undetermined states.
   * Also the input should be updated if the precision setting got modified.
   */
  private shouldUpdateInputControl(
    value: TextInputValue,
    oldProps: PropsOf<'textInput'> | undefined,
    newProps: PropsOf<'textInput'> | undefined,
  ): boolean {
    if (this.inputConfig?.type === 'number') {
      if (!isNullish(value) && !this.formControl.value) {
        return true;
      }
      const formControlValue = handleFormat(
        Number(this.formControl.value),
        newProps?.content?.input,
      );

      return (
        oldProps?.content?.input?.precision !== newProps?.content?.input?.precision ||
        oldProps?.content?.input?.format !== newProps?.content?.input?.format ||
        Number(value) !== formControlValue
      );
    } else if (isGeoPoint(value)) {
      return this.formControl.value !== formatGeoPoint(value);
    } else {
      return value !== this.formControl.value;
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title, container } = content || {};
    const { general, input, inputFont, labelBlock } = style || {};
    if (!style || !title || !general || !input || !inputFont || !container || !labelBlock) {
      return;
    }

    const inputBg = getBackground(input.fill);
    const controlStyles: Record<string, string> = {};
    const validationStyles: Record<string, string> = {};
    const inputWrapperStyles: Record<string, string> = {
      background: inputBg,
      color: input.value.color1,
      ...makeFontStyles(inputFont, { lineHeight: !this.isParagraph }),
    };
    const passwordIconStyles: Record<string, string> = {
      background: inputBg,
      color: input.value.color1,
    };
    const inputStyles: Record<string, string> = {
      ...makeFontStyles(inputFont, { lineHeight: !this.isParagraph }),
      ...makeFontStyleStyles(inputFont.style),
      ['font-weight']: this.fontsService.getFontWeight(inputFont.fontFamily, inputFont.fontWeight),
    };

    if (title.enabled !== false) {
      if (title.position === 'top') {
        controlStyles['flex-direction'] = `column`;
      } else {
        validationStyles['margin-left'] = title.width;
        validationStyles['padding-left'] = '8px';
        inputWrapperStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    this.labelVerticalAlign =
      this.isParagraph && title.position === 'left' ? title.align.vertical : undefined;

    if (input.border.selectorType !== 'none') {
      inputWrapperStyles['border'] = `1px solid ${input.border.color1}`;
      passwordIconStyles['border'] = `1px solid ${input.border.color1}`;
    }
    if (input.radius !== '0px') {
      inputWrapperStyles['border-radius'] = input.radius;
      passwordIconStyles['border-radius'] = input.radius;
    }

    this.controlStyles = controlStyles;
    this.validationStyles = validationStyles;
    this.inputWrapperStyles = inputWrapperStyles;
    this.passwordIconStyles = passwordIconStyles;
    this.inputStyles = inputStyles;

    const currentTheme = this.themesService.activeTheme?.value;
    this.stylesCleanup.update(
      this.editorToolbarKey,
      `
      .${this.editorToolbarKey}.tox.tox-tinymce.tox-tinymce-inline.tox-tinymce--toolbar-sticky-off { visibility: visible !important; }
      .${this.editorToolbarKey} * {
        background: ${currentTheme?.fill} !important;
        color: ${currentTheme?.title} !important;
      }`,
    );
  }

  private updateFormControl(): void {
    const input = this.props?.content?.input;
    if (!input) {
      return;
    }

    const format = this.format ?? input?.format;
    let precision: typeof this.precision = null;
    if (!isNullish(this.precision)) {
      precision = this.precision;
    } else if (!isNullish(input?.precision)) {
      precision = input?.precision;
    }
    const foundConfig = getInputValidationConfig(format);
    const usePrecision = foundConfig?.type === 'number' && precision !== null;

    this.elementFormat = format;
    this.inputStep = usePrecision && !isNullish(precision) ? Number(`1e${-precision}`) : 'any';

    // Apply config
    if (foundConfig && foundConfig !== this.inputConfig) {
      this.inputConfig = foundConfig;
    }

    // Configure prefix and suffix
    if (format === 'percentage') {
      this.prefix = '';
      this.suffix = '%';
    } else if (format === 'currency') {
      const format = getCurrencyFormat(this.currency, false);
      this.prefix = format.prefix;
      this.suffix = format.suffix;
    } else {
      this.prefix = '';
      this.suffix = '';
    }
  }

  private updateOutput(data: TextInputValue, source: TextInputUpdateSource = 'input'): void {
    if (this.inputConfig.type === 'number' && typeof data === 'string') {
      const hasDataToEvaluate = !isNullish(data) && data.trim() !== '';
      data = hasDataToEvaluate ? Number(data) : null;
      if (data !== null && isNaN(data)) {
        return;
      } // Ignore NaN values
    } else if (this.elementFormat === 'duration' && typeof data === 'string' && data) {
      if (!this.durationFormat) {
        return;
      }
      data = formatDurationForSaving(
        data,
        this.durationFormat ?? this.props?.content.input?.duration,
      );
    } else if (this.elementFormat === 'geopoint' && typeof data === 'string') {
      data = createGeoPointFromString(data);
    }
    this.value = data;
    if (this.changeOnBlur && source === 'input' && this.wasEmitted) {
      return;
    }
    if (this.outputEmitTracker.shouldEmit(data)) {
      this.output.emit({
        eventType: 'change',
        data,
        isInitialData: !this.wasEmitted,
        schema: this.getEventSchema('change'),
      });
      this.wasEmitted = true;
    }
  }

  private commitChange(): void {
    if (!this.changeOnBlur) {
      return;
    }
    const input = this.props?.content?.input;
    const options = {
      ...(!!input && this.props?.content?.input),
      phoneCountryCode: this.getCountryCode(),
    } as {
      format: TableColumnFormat | undefined;
      precision: NumberPrecision | undefined;
      phoneCountryCode?: CountryCode;
    };
    const value =
      this.isRichText && this.editor?.editor
        ? this.editor.editor.getContent()
        : handleFormat(this.formControl.value, options);
    this.updateOutput(value, 'blur');
  }

  /**
   * Used to populate the field when the data comes from the input
   * or ensures the proper display format after the user blurs the input field
   */
  private updateInputControlAndPreformat(data: TextInputValue): void {
    if (!this.dataValueFeaturesFetched) {
      return;
    }

    let value = data ?? '';

    if (this.inputConfig.type === 'number') {
      const numberValue = this.enforcePrecision(value);
      value = numberValue !== 'NaN' ? numberValue : '0';
    } else if (this.elementFormat === 'duration' && typeof value === 'string' && this.value) {
      const format = this.durationFormat ?? this.props?.content.input?.duration;
      const currentDurationFormat = getDurationFormatFromDuration(value) ?? format;
      value = formatDuration(formatDurationForSaving(value, currentDurationFormat), format);
    } else if (isGeoPoint(data)) {
      value = formatGeoPoint(data);
    } else if (this.format === 'url' && typeof value === 'string') {
      value = value.trim();
    }

    this.formControl.setValue(value);
  }

  private enforcePrecision(data: string | number | GeoPoint | null): string {
    if (data === '' || data === undefined) {
      // If value is not required allow to have empty value (null)
      data = this.defaultValue ?? null;
    }

    if (data === null) {
      // Empty value is stored as an empty string
      return '';
    }

    const format = this.format ?? this.props?.content?.input?.format;
    let result = Number(data);
    const isFixedPrecision = format === 'currency'; // Currency field should have fixed precision
    let precision: typeof this.precision = null;
    if (!isNullish(this.precision)) {
      precision = this.precision;
    } else if (!isNullish(this.props?.content.input?.precision)) {
      precision = this.props?.content.input?.precision;
    }

    if (format === 'percentage') {
      result *= 100;
    }

    return precision === null
      ? result.toString() // Just reformat if full precision used
      : isFixedPrecision
        ? result.toFixed(precision)
        : roundToPrecision(result, precision).toString();
  }

  private makeEditorSelection(goToEnd = false): void {
    if (!this.isRichText) {
      const input = this.inputText?.nativeElement;
      if (!input) {
        return;
      }

      const point = goToEnd ? input.value.length : 0;
      input.focus();
      const inputType = input.type;
      input.type = 'text';
      input.selectionStart = point;
      input.selectionEnd = point;
      input.type = inputType;
      return;
    }

    if (!this.editor?.editor) {
      return;
    }

    const editorNodes = this.editor.editor.dom.getRoot().childNodes;
    const node = goToEnd ? editorNodes[editorNodes.length - 1] : editorNodes[0];

    this.editor.editor.selection.select(node);
    this.editor.editor.selection.collapse(!goToEnd);
  }

  private formatInputPhoneNumberInput(value: string): void {
    if (typeof this.value === 'string' && this.format === 'phoneNumber') {
      const defaultCountry = this.getCountryCode();
      const defaultCallingProps = allCountries.find((e) => e.iso2.toUpperCase() === defaultCountry);
      const dialCodePrefix = defaultCallingProps?.format.startsWith('+') ? '+' : '';
      const defaultCallingCode = defaultCallingProps
        ? `${dialCodePrefix}${defaultCallingProps.dialCode}`
        : undefined;
      const formattedValue = new AsYouType({ defaultCountry, defaultCallingCode }).input(value);
      if (regexpMatchLength(digitRegex, this.value || '') > 4 && this.value !== formattedValue) {
        this.formControl.patchValue(formattedValue, { emitEvent: false });
      }
    }
  }

  private getCountryCode(): CountryCode {
    return (getCountryFromLocale(this.culture) ?? userBrowserCountry()) as CountryCode;
  }

  private tryToolbarStyling(triesLeft = EDITOR_TOOLBAR_CHECK_TRIES): void {
    if (!this.isRichText) {
      return;
    }
    const toolbar = getTinyMceToolbarElement();
    if (!toolbar && triesLeft) {
      setTimeout(() => this.tryToolbarStyling(triesLeft - 1), EDITOR_TOOLBAR_CHECK_TIMEOUT);
      return;
    }
    if (toolbar) {
      toolbar.classList.add(this.editorToolbarKey);
    }
  }

  private tryFocusEditor(timeout = 50, maxRetries = 5): void {
    if (maxRetries === 0) {
      return;
    }
    setTimeout(() => {
      if (this.editorBody) {
        this.editorBody.focus();
        this.tryToolbarStyling();
        this.makeEditorSelection(true);
        return;
      }
      this.tryFocusEditor(50, maxRetries - 1);
    }, timeout);
  }

  private handleMinMax(value: string | number): void {
    if (
      this.elementFormat !== 'number' &&
      this.elementFormat !== 'currency' &&
      this.elementFormat !== 'percentage'
    ) {
      return;
    }

    if (typeof value !== 'number' && value !== '') {
      try {
        value = Number.parseFloat(value);
      } catch {
        value = this.minimum ?? 0;
      }
    }

    if (typeof value !== 'number') {
      return;
    }

    if (
      this.formId &&
      ((!isNullish(this.minimum) && value < this.minimum) ||
        (!isNullish(this.maximum) && value > this.maximum))
    ) {
      this.store.dispatch(runtimeActions.formValidationTrigger({ formElementId: this.formId }));
    }
  }
}
